export interface AuthUser {
  id: string;
  nome: string;
  email: string;
  role: "ADMIN" | "OPERATOR";
}

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  role: "ADMIN" | "OPERATOR";
  ativo: number;
  criado_em: string;
}

export interface Edital {
  id: string;
  codigo_local: string;
  mes: number;
  ano: number;
  situacao: string;
  motivo_situacao: string | null;
  total_lotes: number;
  ultima_verificacao: string;
}

export const isEditalAberto = (situacao: string | null | undefined): boolean => {
  if (!situacao) return false;
  const s = situacao.trim().toUpperCase();
  return s === "ABERTO" || s === "ATIVO" || s === "EM DISPUTA";
};

export interface Lote {
  id: string;
  edital_id: string;
  nr_atribuido: number;
  tipo: string | null;
  cidade: string | null;
  valor_minimo: number;
  valor_avaliacao: number | null;
  qtde_itens_distintos: number;
  qtde_total_unidades: number;
  custo_unitario_estimado: number | null;
  permite_pf: number;
  lote_heterogeneo: number;
  sem_itens: number;
  extraido_em: string;
  preview_itens?: PreviewItem[];
  top_marcas?: { marca: string; total_qtd: number }[];
}

export interface TipoDisponivel {
  tipo: string;
  total_lotes: number;
}

export interface PreviewItem {
  descricao: string;
  marca: string;
  quantidade: number;
  un_medida: string;
}

export interface ItemConsolidado {
  id: string;
  descricao: string;
  marca: string;
  categoria: string;
  un_medida: string;
  quantidade_total: number;
  total_ocorrencias: number;
  custo_unitario_estimado: number | null;
  custo_total_estimado: number | null;
  percentual_do_lote: number;
}

export interface ResumoMarca {
  marca: string;
  total_unidades: number;
  produtos_distintos: number;
  percentual_do_lote: number;
}

export interface EstatisticasConsolidacao {
  total_linhas_rfb: number;
  total_produtos_unicos: number;
  total_unidades: number;
  taxa_reducao_duplicatas: number;
}

export interface Item {
  id: string;
  lote_id: string;
  recinto_armazenador: string | null;
  nr_referencia: string | null;
  quantidade: number;
  un_medida: string | null;
  descricao: string;
  marca: string;
  categoria: string;
  custo_unitario_estimado: number | null;
}

export interface AvisoErrata {
  id: string;
  lote_id: string;
  data: string | null;
  tipo: string;
  texto: string;
}

export interface Imagem {
  id: string;
  lote_id: string;
  url_grande: string;
  url_miniatura: string;
  largura: number | null;
  altura: number | null;
}

export interface LoteNavegacao {
  anterior: { id: string; nr_atribuido: number } | null;
  proximo: { id: string; nr_atribuido: number } | null;
}

export interface LoteDetailResponse {
  lote: Lote;
  itens: Item[];
  itens_consolidados: ItemConsolidado[];
  resumo_marcas: ResumoMarca[];
  estatisticas_consolidacao: EstatisticasConsolidacao;
  avisos: AvisoErrata[];
  imagens: Imagem[];
  navegacao?: LoteNavegacao;
}

export interface ResumoItem {
  categoria: string;
  marca: string;
  qtde_itens: number;
  total_unidades: number;
  custo_medio_estimado: number | null;
  lotes_distintos: number;
}

export interface ResumoPorCategoria {
  categoria: string;
  qtde_itens: number;
  total_unidades: number;
}

export interface ResumoResponse {
  editalId: string;
  resumoCategoriaMarca: ResumoItem[];
  resumoPorCategoria: ResumoPorCategoria[];
}

export interface EstatisticaAgioTipo {
  tipo: string;
  n_lotes: number;
  agio_mediana: number;
  agio_medio: number;
  agio_min: number;
  agio_max: number;
  valor_minimo_total: number;
  valor_arrematado_total: number;
}

export interface EstatisticasHistoricoResponse {
  editalId: string;
  total_lotes_arrematados: number;
  agio_mediana_geral: number;
  agio_medio_geral: number;
  valor_minimo_acumulado: number;
  valor_arrematado_acumulado: number;
  estatisticasPorTipo: EstatisticaAgioTipo[];
}

export interface MarkupCalculationResponse {
  lote_id: string;
  premissas: {
    valor_minimo: number;
    lance_simulado: number;
    receita_venda_projetada: number;
    markup_alvo: number;
    aliquota_icms: number;
    taxa_comissao: number;
    custo_frete: number;
    outras_despesas: number;
  };
  resultado_lance_simulado: {
    custo_impostos_taxas: number;
    custo_total_acumulado: number;
    lucro_liquido: number;
    margem_liquida_pct: number;
    markup_real: number;
  };
  analise_lance_teto: {
    lance_teto_calculado: number;
    status_viabilidade: "EXCELENTE" | "MODERADO" | "INVIAVEL";
    motivo: string;
    folga_reais: number;
  };
}

export interface SimularLanceResponse {
  lote_id: string;
  parametros: {
    lance_pretendido: number;
    valor_minimo: number;
    valor_avaliacao: number;
    qtde_total_unidades: number;
    aliquota_icms: number;
    taxa_comissao: number;
    custo_frete: number;
    outras_despesas: number;
  };
  custos_adicionais: {
    custo_icms: number;
    custo_taxas: number;
    custo_impostos_taxas: number;
    custo_frete: number;
    outras_despesas: number;
    desembolso_total: number;
  };
  analise_unitaria: {
    custo_unitario_medio: number;
    custo_unitario_lance_puro: number;
    avaliacao_unitaria: number | null;
  };
  benchmark_avaliacao: {
    valor_avaliacao: number;
    percentual_da_avaliacao: number | null;
    desconto_sobre_avaliacao: number | null;
    classificacao: "OPORTUNIDADE_EXCELENTE" | "COMPETITIVO_MODERADO" | "RISCO_ELEVADO";
    motivo: string;
  };
  previsao_lance_estimado: {
    lance_estimado_agio: number;
    lance_estimado_avaliacao: number | null;
    mediana_agio_categoria_pct: number;
    faixa_oportunidade_max: number;
    faixa_teto_recomendado: number;
  };
}

